import { chipPalette } from "../../../theme/overrides";

export interface RecentContext {
  id: string;
  clientIndex: number;
  customerId: string;
  programIndex: number;
  userTypeIndex?: number;
  initials: string;
  client: string;
  /** "All branches · all departments", "Sales rep · Kelvin Ochoa" */
  detail: string;
  program: string;
  tint: string;
  fg: string;
  active?: boolean;
}

export interface ViewAsOption {
  id: string;
  title: string;
  body: string;
  /** The underlying UserType.Name, shown small so power users can map it. */
  code: string;
  userTypeIndex: number;
}

/**
 * A context's identity, from the store's selection objects. The id is what
 * dedupes the recents list and what marks the active row in the popover, so
 * every producer must build it the same way.
 */
export const contextIdOf = (client: any, program: any, userType?: any): string =>
  [client?.client_index, program?.program_index, userType?.client_user_type_index]
    .filter((part) => part !== undefined && part !== null)
    .join(":");

/**
 * Build a RecentContext from the current selection.
 *
 * One factory so the chooser and the rail cannot disagree about a context's id
 * — the id is what dedupes the recents list, so two spellings would show the
 * same dashboard twice.
 */
export const toRecentContext = (args: {
  client: any;
  program: any;
  userType?: any;
  subScope?: string;
  programLabel: string;
}): RecentContext => {
  const { client, program, userType, subScope, programLabel } = args;
  const name: string = client?.customer_name ?? "";

  return {
    id: contextIdOf(client, program, userType),
    clientIndex: client?.client_index,
    customerId: client?.customer_id,
    programIndex: program?.program_index,
    userTypeIndex: userType?.client_user_type_index,
    initials: name.slice(0, 2).toUpperCase(),
    client: name,
    detail: subScope ?? userType?.client_user_type_name ?? "All branches · all departments",
    program: programLabel,
    // Deterministic, so a client keeps the same chip colour between sessions.
    tint: chipPalette[hashIndex(name, chipPalette.length)],
    fg: "#fff"
  };
};

const hashIndex = (value: string, buckets: number) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = (hash * 31 + value.charCodeAt(i)) % 100000;
  return hash % buckets;
};
